import React, { useState } from 'react';
import { Calendar, Clock, Users, MapPin, Video, ChevronLeft, FileText, Download, MessageSquare, Send, CheckCircle, User, Edit, Trash2, X, Plus } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
export function MeetingDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('agenda');
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  // Mock meeting data - in a real app, this would be fetched from an API
  const meeting = {
    id: 1,
    title: 'Finance Committee Monthly Review',
    committee: 'Finance Committee',
    date: '2023-09-15',
    time: '10:00 AM',
    duration: '60 minutes',
    type: 'Online',
    location: 'https://zoom.us/j/1234567890',
    description: 'Monthly review of financial reports and budget planning for Q4.',
    status: 'upcoming',
    organizer: 'Mohammed Al Hashimi',
    attendees: [{
      id: 1,
      name: 'Mohammed Al Hashimi',
      role: 'Chairperson',
      status: 'confirmed'
    }, {
      id: 2,
      name: 'Aisha Al Mansoori',
      role: 'Secretary',
      status: 'confirmed'
    }, {
      id: 3,
      name: 'Omar Al Shamsi',
      role: 'Member',
      status: 'pending'
    }, {
      id: 4,
      name: 'Fatima Al Zaabi',
      role: 'Member',
      status: 'confirmed'
    }, {
      id: 5,
      name: 'Ahmed Al Dhaheri',
      role: 'Member',
      status: 'declined'
    }],
    agenda: [{
      id: 1,
      title: 'Review of Previous Minutes',
      duration: '10 mins',
      presenter: 'Aisha Al Mansoori'
    }, {
      id: 2,
      title: 'Q3 Financial Performance Review',
      duration: '20 mins',
      presenter: 'Mohammed Al Hashimi'
    }, {
      id: 3,
      title: 'Budget Planning for Q4',
      duration: '15 mins',
      presenter: 'Fatima Al Zaabi'
    }, {
      id: 4,
      title: 'Open Discussion',
      duration: '10 mins',
      presenter: 'All'
    }, {
      id: 5,
      title: 'Action Items & Next Steps',
      duration: '5 mins',
      presenter: 'Mohammed Al Hashimi'
    }],
    documents: [{
      id: 1,
      name: 'Previous_Meeting_Minutes.pdf',
      type: 'PDF',
      size: '1.2 MB'
    }, {
      id: 2,
      name: 'Q3_Financial_Report.xlsx',
      type: 'XLSX',
      size: '2.4 MB'
    }, {
      id: 3,
      name: 'Q4_Budget_Draft.xlsx',
      type: 'XLSX',
      size: '1.8 MB'
    }],
    minutes: {
      notes: [{
        id: 1,
        agenda_item: 'Review of Previous Minutes',
        content: 'Previous minutes were reviewed and approved without changes.'
      }, {
        id: 2,
        agenda_item: 'Q3 Financial Performance Review',
        content: 'Mohammed presented the Q3 financial performance. Revenue exceeded targets by 12%, while expenses were 5% under budget. The committee discussed the main drivers behind these positive results.'
      }, {
        id: 3,
        agenda_item: 'Budget Planning for Q4',
        content: 'Fatima presented the draft Q4 budget. The committee suggested increasing the marketing allocation by 10% and reducing administrative expenses by 5%. A revised budget will be presented at the next meeting.'
      }, {
        id: 4,
        agenda_item: 'Open Discussion',
        content: 'The committee discussed the need for improved financial reporting tools. IT department will be consulted on available options.'
      }, {
        id: 5,
        agenda_item: 'Action Items & Next Steps',
        content: 'Fatima will revise the Q4 budget. Mohammed will coordinate with IT regarding financial reporting tools. Next meeting scheduled for October 15.'
      }],
      action_items: [{
        id: 1,
        description: 'Revise Q4 budget with suggested changes',
        assignee: 'Fatima Al Zaabi',
        due_date: '2023-09-22'
      }, {
        id: 2,
        description: 'Coordinate with IT regarding financial reporting tools',
        assignee: 'Mohammed Al Hashimi',
        due_date: '2023-09-30'
      }, {
        id: 3,
        description: 'Prepare draft agenda for next meeting',
        assignee: 'Aisha Al Mansoori',
        due_date: '2023-10-08'
      }]
    },
    comments: [{
      id: 1,
      user: 'Mohammed Al Hashimi',
      avatar: 'M',
      date: '2023-09-10',
      time: '14:30',
      content: 'Please review the attached Q3 financial report before the meeting.'
    }, {
      id: 2,
      user: 'Aisha Al Mansoori',
      avatar: 'A',
      date: '2023-09-11',
      time: '09:15',
      content: "I've prepared the minutes from our last meeting. Please let me know if any changes are needed."
    }, {
      id: 3,
      user: 'Fatima Al Zaabi',
      avatar: 'F',
      date: '2023-09-12',
      time: '11:45',
      content: "The Q4 budget draft is now available for review. I've highlighted the key changes from Q3."
    }]
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'PDF':
        return <FileText size={16} className="text-red-500" data-id="element-1353" />;
      case 'DOCX':
        return <FileText size={16} className="text-blue-500" data-id="element-1354" />;
      case 'XLSX':
        return <FileText size={16} className="text-green-500" data-id="element-1355" />;
      case 'PPTX':
        return <FileText size={16} className="text-orange-500" data-id="element-1356" />;
      default:
        return <FileText size={16} className="text-gray-500" data-id="element-1357" />;
    }
  };
  return <div data-id="element-1358">
      <div className="flex items-center mb-6" data-id="element-1359">
        <Link to="/meetings" className="flex items-center text-gray-600 hover:text-gray-900 mr-4" data-id="element-1360">
          <ChevronLeft size={20} className="mr-1" data-id="element-1361" />
          Back to Meetings
        </Link>
        <h1 className="text-2xl font-bold" data-id="element-1362">{meeting.title}</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1363">
        {/* Meeting Details */}
        <div className="lg:col-span-2" data-id="element-1364">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1365">
            <div className="flex justify-between items-start mb-4" data-id="element-1366">
              <div data-id="element-1367">
                <h2 className="text-lg font-semibold" data-id="element-1368">{meeting.title}</h2>
                <p className="text-gray-500" data-id="element-1369">{meeting.committee}</p>
              </div>
              <div data-id="element-1370">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium" data-id="element-1371">
                  {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1372">
              <div data-id="element-1373">
                <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1374">
                  Date & Time
                </h3>
                <div className="flex items-center" data-id="element-1375">
                  <Calendar size={16} className="text-gray-500 mr-2" data-id="element-1376" />
                  <span data-id="element-1377">
                    {meeting.date}, {meeting.time} ({meeting.duration})
                  </span>
                </div>
              </div>
              <div data-id="element-1378">
                <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1379">
                  Meeting Type
                </h3>
                <div className="flex items-center" data-id="element-1380">
                  {meeting.type === 'Online' ? <Video size={16} className="text-blue-500 mr-2" data-id="element-1381" /> : <MapPin size={16} className="text-red-500 mr-2" data-id="element-1382" />}
                  <span data-id="element-1383">{meeting.type}</span>
                </div>
              </div>
              <div data-id="element-1384">
                <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1385">
                  {meeting.type === 'Online' ? 'Meeting Link' : 'Location'}
                </h3>
                <div className="flex items-center" data-id="element-1386">
                  {meeting.type === 'Online' ? <a href={meeting.location} className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1387">
                      Join Meeting
                    </a> : <span data-id="element-1388">{meeting.location}</span>}
                </div>
              </div>
              <div data-id="element-1389">
                <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1390">
                  Organizer
                </h3>
                <div className="flex items-center" data-id="element-1391">
                  <div className="h-6 w-6 bg-gray-700 rounded-full flex items-center justify-center text-white text-xs font-medium mr-2" data-id="element-1392">
                    {meeting.organizer.charAt(0)}
                  </div>
                  <span data-id="element-1393">{meeting.organizer}</span>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1394">
              <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1395">
                Description
              </h3>
              <p className="text-gray-700" data-id="element-1396">{meeting.description}</p>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1397">
              <button className="px-3 py-1 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-1398">
                <Edit size={16} className="inline mr-1" data-id="element-1399" />
                Edit Meeting
              </button>
              <button className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors" data-id="element-1400">
                <X size={16} className="inline mr-1" data-id="element-1401" />
                Cancel Meeting
              </button>
              <button className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors" onClick={() => setShowAttendanceModal(true)} data-id="element-1402">
                <CheckCircle size={16} className="inline mr-1" data-id="element-1403" />
                Take Attendance
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-1404">
            <div className="flex border-b border-gray-200" data-id="element-1405">
              <button className={`px-4 py-3 font-medium ${activeTab === 'agenda' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('agenda')} data-id="element-1406">
                Agenda
              </button>
              <button className={`px-4 py-3 font-medium ${activeTab === 'attendees' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('attendees')} data-id="element-1407">
                Attendees
              </button>
              <button className={`px-4 py-3 font-medium ${activeTab === 'documents' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('documents')} data-id="element-1408">
                Documents
              </button>
              <button className={`px-4 py-3 font-medium ${activeTab === 'minutes' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('minutes')} data-id="element-1409">
                Minutes
              </button>
            </div>
            <div className="p-6" data-id="element-1410">
              {/* Agenda Tab */}
              {activeTab === 'agenda' && <div data-id="element-1411">
                  <div className="flex justify-between items-center mb-4" data-id="element-1412">
                    <h3 className="text-lg font-semibold" data-id="element-1413">Meeting Agenda</h3>
                    <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors flex items-center" data-id="element-1414">
                      <Plus size={14} className="mr-1" data-id="element-1415" />
                      Add Item
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-1416">
                    {meeting.agenda.map((item, index) => <div key={item.id} className="flex items-start border border-gray-200 rounded-lg p-4" data-id="element-1417">
                        <div className="h-6 w-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium mr-3 mt-1" data-id="element-1418">
                          {index + 1}
                        </div>
                        <div className="flex-1" data-id="element-1419">
                          <div className="flex justify-between" data-id="element-1420">
                            <h4 className="font-medium" data-id="element-1421">{item.title}</h4>
                            <span className="text-sm text-gray-500" data-id="element-1422">
                              {item.duration}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500" data-id="element-1423">
                            Presenter: {item.presenter}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}
              {/* Attendees Tab */}
              {activeTab === 'attendees' && <div data-id="element-1424">
                  <div className="flex justify-between items-center mb-4" data-id="element-1425">
                    <h3 className="text-lg font-semibold" data-id="element-1426">Meeting Attendees</h3>
                    <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors flex items-center" data-id="element-1427">
                      <Plus size={14} className="mr-1" data-id="element-1428" />
                      Add Attendee
                    </button>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1429">
                    <table className="w-full" data-id="element-1430">
                      <thead data-id="element-1431">
                        <tr className="bg-gray-50" data-id="element-1432">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1433">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1434">
                            Role
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1435">
                            Status
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1436">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-1437">
                        {meeting.attendees.map(attendee => <tr key={attendee.id} data-id="element-1438">
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-1439">
                              <div className="flex items-center" data-id="element-1440">
                                <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-1441">
                                  {attendee.name.charAt(0)}
                                </div>
                                <span className="font-medium" data-id="element-1442">
                                  {attendee.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-1443">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${attendee.role === 'Chairperson' ? 'bg-blue-100 text-blue-800' : attendee.role === 'Secretary' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1444">
                                {attendee.role}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-1445">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(attendee.status)}`} data-id="element-1446">
                                {attendee.status.charAt(0).toUpperCase() + attendee.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-1447">
                              <button className="text-red-500 hover:text-red-700 mr-2" data-id="element-1448">
                                <X size={16} data-id="element-1449" />
                              </button>
                              <button className="text-blue-500 hover:text-blue-700" data-id="element-1450">
                                <Edit size={16} data-id="element-1451" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}
              {/* Documents Tab */}
              {activeTab === 'documents' && <div data-id="element-1452">
                  <div className="flex justify-between items-center mb-4" data-id="element-1453">
                    <h3 className="text-lg font-semibold" data-id="element-1454">Meeting Documents</h3>
                    <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors flex items-center" data-id="element-1455">
                      <Plus size={14} className="mr-1" data-id="element-1456" />
                      Upload Document
                    </button>
                  </div>
                  <div className="space-y-3" data-id="element-1457">
                    {meeting.documents.map(document => <div key={document.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-id="element-1458">
                        <div className="flex items-center" data-id="element-1459">
                          {getFileIcon(document.type)}
                          <span className="ml-2 font-medium" data-id="element-1460">
                            {document.name}
                          </span>
                          <span className="ml-2 text-xs text-gray-500" data-id="element-1461">
                            {document.size}
                          </span>
                        </div>
                        <div className="flex space-x-2" data-id="element-1462">
                          <button className="p-1 text-blue-500 hover:text-blue-700" data-id="element-1463">
                            <FileText size={16} data-id="element-1464" />
                          </button>
                          <button className="p-1 text-green-500 hover:text-green-700" data-id="element-1465">
                            <Download size={16} data-id="element-1466" />
                          </button>
                          <button className="p-1 text-red-500 hover:text-red-700" data-id="element-1467">
                            <Trash2 size={16} data-id="element-1468" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>}
              {/* Minutes Tab */}
              {activeTab === 'minutes' && <div data-id="element-1469">
                  <div className="flex justify-between items-center mb-4" data-id="element-1470">
                    <h3 className="text-lg font-semibold" data-id="element-1471">Meeting Minutes</h3>
                    <div className="space-x-2" data-id="element-1472">
                      <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors" data-id="element-1473">
                        <Edit size={14} className="inline mr-1" data-id="element-1474" />
                        Edit Minutes
                      </button>
                      <button className="px-3 py-1 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition-colors" data-id="element-1475">
                        <Download size={14} className="inline mr-1" data-id="element-1476" />
                        Export
                      </button>
                    </div>
                  </div>
                  <div className="mb-6" data-id="element-1477">
                    <h4 className="font-medium mb-3" data-id="element-1478">Discussion Notes</h4>
                    <div className="space-y-4" data-id="element-1479">
                      {meeting.minutes.notes.map(note => <div key={note.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1480">
                          <h5 className="font-medium mb-2" data-id="element-1481">
                            {note.agenda_item}
                          </h5>
                          <p className="text-gray-700" data-id="element-1482">{note.content}</p>
                        </div>)}
                    </div>
                  </div>
                  <div data-id="element-1483">
                    <h4 className="font-medium mb-3" data-id="element-1484">Action Items</h4>
                    <div className="overflow-x-auto" data-id="element-1485">
                      <table className="w-full" data-id="element-1486">
                        <thead data-id="element-1487">
                          <tr className="bg-gray-50" data-id="element-1488">
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1489">
                              Task
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1490">
                              Assigned To
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1491">
                              Due Date
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1492">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1493">
                          {meeting.minutes.action_items.map(item => <tr key={item.id} data-id="element-1494">
                              <td className="px-4 py-2" data-id="element-1495">
                                <div className="font-medium" data-id="element-1496">
                                  {item.description}
                                </div>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap" data-id="element-1497">
                                {item.assignee}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap" data-id="element-1498">
                                {item.due_date}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap" data-id="element-1499">
                                <button className="text-orange-500 hover:text-orange-700 mr-2" data-id="element-1500">
                                  <Edit size={16} data-id="element-1501" />
                                </button>
                                <button className="text-red-500 hover:text-red-700" data-id="element-1502">
                                  <Trash2 size={16} data-id="element-1503" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
        {/* Sidebar */}
        <div data-id="element-1504">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1505">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1506">Quick Actions</h3>
            <div className="space-y-3" data-id="element-1507">
              <button className="w-full flex items-center justify-center p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors" data-id="element-1508">
                <Video size={18} className="mr-2" data-id="element-1509" />
                Join Meeting
              </button>
              <button className="w-full flex items-center justify-center p-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors" data-id="element-1510">
                <FileText size={18} className="mr-2" data-id="element-1511" />
                View Agenda
              </button>
              <button className="w-full flex items-center justify-center p-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors" data-id="element-1512">
                <MessageSquare size={18} className="mr-2" data-id="element-1513" />
                Send Reminder
              </button>
              <button className="w-full flex items-center justify-center p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors" data-id="element-1514">
                <Calendar size={18} className="mr-2" data-id="element-1515" />
                Add to Calendar
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1516">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1517">Comments</h3>
            <div className="space-y-4 mb-4" data-id="element-1518">
              {meeting.comments.map(comment => <div key={comment.id} className="flex" data-id="element-1519">
                  <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3 flex-shrink-0" data-id="element-1520">
                    {comment.avatar}
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg flex-1" data-id="element-1521">
                    <div className="flex justify-between items-center mb-1" data-id="element-1522">
                      <span className="font-medium" data-id="element-1523">{comment.user}</span>
                      <span className="text-xs text-gray-500" data-id="element-1524">
                        {comment.date} at {comment.time}
                      </span>
                    </div>
                    <p className="text-sm" data-id="element-1525">{comment.content}</p>
                  </div>
                </div>)}
            </div>
            <div className="flex items-center mt-4" data-id="element-1526">
              <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-1527">
                J
              </div>
              <div className="flex-1 relative" data-id="element-1528">
                <input type="text" placeholder="Add a comment..." className="w-full border border-gray-200 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-1529" />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-orange-500" data-id="element-1530">
                  <Send size={18} data-id="element-1531" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Attendance Modal */}
      {showAttendanceModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1532">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6" data-id="element-1533">
            <div className="flex justify-between items-center mb-4" data-id="element-1534">
              <h3 className="text-lg font-semibold" data-id="element-1535">Take Attendance</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowAttendanceModal(false)} data-id="element-1536">
                <X size={20} data-id="element-1537" />
              </button>
            </div>
            <div className="mb-4" data-id="element-1538">
              <p className="text-gray-500 mb-2" data-id="element-1539">
                Mark attendance for: <strong data-id="element-1540">{meeting.title}</strong> on{' '}
                <strong data-id="element-1541">{meeting.date}</strong>
              </p>
            </div>
            <div className="overflow-y-auto max-h-96" data-id="element-1542">
              <table className="w-full" data-id="element-1543">
                <thead data-id="element-1544">
                  <tr className="bg-gray-50" data-id="element-1545">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1546">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1547">
                      Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1548">
                      Attendance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1549">
                  {meeting.attendees.map(attendee => <tr key={attendee.id} data-id="element-1550">
                      <td className="px-4 py-2 whitespace-nowrap" data-id="element-1551">
                        <div className="flex items-center" data-id="element-1552">
                          <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-1553">
                            {attendee.name.charAt(0)}
                          </div>
                          <span className="font-medium" data-id="element-1554">{attendee.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap" data-id="element-1555">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${attendee.role === 'Chairperson' ? 'bg-blue-100 text-blue-800' : attendee.role === 'Secretary' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1556">
                          {attendee.role}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap" data-id="element-1557">
                        <select className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-1558">
                          <option value="present" data-id="element-1559">Present</option>
                          <option value="absent" data-id="element-1560">Absent</option>
                          <option value="excused" data-id="element-1561">Excused</option>
                          <option value="late" data-id="element-1562">Late</option>
                        </select>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-1563">
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" onClick={() => setShowAttendanceModal(false)} data-id="element-1564">
                Cancel
              </button>
              <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" onClick={() => setShowAttendanceModal(false)} data-id="element-1565">
                Save Attendance
              </button>
            </div>
          </div>
        </div>}
    </div>;
}